/**
 * The Call Script column of the Review view (BV-1575) — the case details page's only
 * Call Script surface since the standalone Human Call Script tab was folded into Review.
 *
 * Reads the workspace (working set, debounced persistence, disposition engine, heard
 * marks, script model) from `HumanCallWorkspaceProvider` and renders the panel header plus
 * the guided `ScriptFlow`. The provider owns the single instance and is mounted only for
 * the case/call that shows the script, so the workspace still never runs on an ordinary
 * case-details render — and the Output pane's field board now drives the SAME working set
 * rather than a second, racing copy (BV-1575).
 *
 * The caller decides when that is: the selected call must be the most recent human call on
 * the case. Older calls keep the untouched three-column Review layout.
 *
 * Editability is the caller's `editable` / `callActive` (from `useHumanCallEditState`), so
 * the column is a live capture surface during the call and read-only reference after it.
 *
 * The only view state owned here is which sections are open (in `ScriptFlow`, driven
 * imperatively). The All / Missed / Required narrowing is NOT owned here: the Output
 * board renders the single control and this column reads `workspace.filterMode`, so the
 * two panes always agree (LA-7358).
 */
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useHumanCallWorkspaceContext } from './HumanCallWorkspaceContext';
import { ScriptFlow, type ScriptFlowHandle } from './ScriptFlow';
import { useCloneScriptUnits } from './useCloneScriptUnits';

interface ReviewCallScriptColumnProps {
  /** Case id — only for ScriptFlow's File-type fields, which resolve assets by case. */
  caseId: string;
  editable: boolean;
  callActive: boolean;
  /**
   * Output metadata `fields` tree for the selected call (heard-seek already merged in).
   * Passed from the page rather than derived here so the script and the Output board read
   * the identical, call-source-stamped tree. Undefined before extraction has run.
   */
  outputMetaFields?: Record<string, unknown>;
  /** The call's EXTRACTED metadata `fields` tree — the source that actually carries
   *  per-field evidence/reasoning. Same path keys as the data tree. */
  extractedMetaFields?: Record<string, unknown>;
  /** The extracted VALUES for this call — powers the per-field Accept affordance. */
  extractedData?: Record<string, unknown>;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
}

export const ReviewCallScriptColumn: React.FC<ReviewCallScriptColumnProps> = ({
  caseId,
  editable,
  callActive,
  outputMetaFields,
  extractedMetaFields,
  extractedData,
  onNavigateToCall,
  onNavigateToAudit,
}) => {
  // The workspace is owned by `HumanCallWorkspaceProvider` and shared with the Output
  // pane's field board, so a value captured in either lands in the same working set
  // (BV-1575). Null only if this column is ever rendered outside that provider.
  const workspace = useHumanCallWorkspaceContext();

  // Collapse-all / expand-all lives in the panel header (inline with the title —
  // matching the "All fields" board). ScriptFlow owns the open state; we trigger it
  // imperatively via the ref and mirror its `{count, anyOpen}` here to show/hide the
  // button and pick the icon. `setScriptCollapse` is a stable setter, so passing it as
  // `onCollapsibleChange` doesn't break ScriptFlow's memoization.
  const scriptFlowRef = useRef<ScriptFlowHandle>(null);
  const [scriptCollapse, setScriptCollapse] = useState<{ count: number; anyOpen: boolean }>({
    count: 0,
    anyOpen: false,
  });

  // The All / Missed / Required narrowing is READ from the workspace, not owned here
  // (LA-7358). The Output board renders the single control; this column used to carry a
  // second copy, so the same case could be narrowed two different ways at once.
  // Unit-level clone (BV-1575): copy ONE question's answers across the entity instances it
  // fanned out to. Deliberately narrower than the Output board's item clone — see
  // useCloneScriptUnits for why a full-item replace is unsafe from a collapsed script.
  // Writes land as a single output revision via `onFieldsChange`, so the disposition
  // engine re-scores once against the complete set rather than field by field.
  const writeClonedFields = useCallback(
    (entries: ReadonlyArray<{ key: string; value: unknown }>) => workspace?.onFieldsChange(entries),
    [workspace],
  );
  const unitClones = useCloneScriptUnits(
    workspace?.model.units ?? [],
    Boolean(editable),
    writeClonedFields,
  );

  // A click on an Output board row asks, via the workspace, for the script to scroll to
  // that field. Keyed on the request object, so clicking the same row again re-scrolls.
  const scriptRevealRequest = workspace?.scriptRevealRequest;
  useEffect(() => {
    if (scriptRevealRequest) scriptFlowRef.current?.revealField(scriptRevealRequest.key);
  }, [scriptRevealRequest]);

  // After every hook, so the hook order stays stable. Only reachable if this column is
  // rendered outside `HumanCallWorkspaceProvider`, which the Review layout never does.
  if (!workspace) return null;

  const {
    model,
    heardAt,
    toggleHeard,
    onFieldChange,
    handleOutputCommit,
    callStartMs,
    isSaving,
    filterMode,
  } = workspace;

  return (
    // The test hook for the Review view's Call Script surface. BV-1575 folded the
    // standalone Human Call Script tab into Review and shipped no test ids, leaving the
    // e2e suite with no way to reach this column except DOM-shape traversal (BV-2120).
    //
    // A BOUNDED flex column, not a single scrolling div (LA-7358). The header used to sit
    // inside the scroller and slid away with the script, so the title, the Saving…
    // indicator and collapse-all were all gone exactly when a rep scrolling a long script
    // needed them. Same three-part shape the Output board uses: bounded root, `shrink-0`
    // header, and one `flex-1 overflow-y-auto` body beneath it.
    <div className="flex min-h-0 flex-1 flex-col" data-testid="review-call-script">
      <div className="flex h-7 shrink-0 items-center justify-between gap-2 bg-background px-4 pt-2">
        {/* Matches the neighbouring pane headers (semibold foreground, not a muted
            sub-label) so all the column titles read consistently. */}
        <h3 className="text-sm font-semibold text-foreground">Call Script</h3>
        <div className="flex items-center gap-1.5">
          {isSaving && <span className="text-xs text-muted-foreground">Saving…</span>}
          {/* No All / Missed / Required control here: the Output board renders the only
              one, and this column follows it via `workspace.filterMode` (LA-7358). */}
          {scriptCollapse.count > 0 && (
            <button
              type="button"
              onClick={() => scriptFlowRef.current?.toggleAll()}
              title={scriptCollapse.anyOpen ? 'Collapse all sections' : 'Expand all sections'}
              aria-label={scriptCollapse.anyOpen ? 'Collapse all sections' : 'Expand all sections'}
              className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            >
              {scriptCollapse.anyOpen ? (
                <ChevronsDownUp className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronsUpDown className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2 pt-2">
        <ScriptFlow
          ref={scriptFlowRef}
          filterMode={filterMode}
          fieldErrors={workspace.fieldErrors}
          onCollapsibleChange={setScriptCollapse}
          units={model.units}
          currentUnitId={model.currentUnitId}
          otherFields={model.otherFields}
          readOnly={!editable}
          callActive={callActive}
          heardAt={heardAt}
          callStartMs={callStartMs}
          onFieldChange={onFieldChange}
          // The multi-code grid's "Same for all" (BV-2742) — one revision for every code.
          onFieldsChange={writeClonedFields}
          onFieldCommit={handleOutputCommit}
          onToggleHeard={toggleHeard}
          caseRecordId={caseId}
          outputMetaFields={outputMetaFields}
          extractedMetaFields={extractedMetaFields}
          extractedData={extractedData}
          onNavigateToCall={onNavigateToCall}
          onNavigateToAudit={onNavigateToAudit}
          unitClones={unitClones}
        />
      </div>
    </div>
  );
};
